// Thin fetch helper for the OpenbooQ backend `/v1/website/*` endpoints.
//
// This replaces the website's direct Supabase data access as part of the
// Supabase -> Cloud SQL migration. All read/write of website content (reviews,
// city tiers, draft cities, address lookups, launch signups) now flows through
// the backend API instead of the public Supabase anon client.
//
// Base URL comes from VITE_API_BASE (inlined at build time by Vite):
//   dev  -> https://dev-api.openbooq.com/api
//   prod -> https://api.openbooq.com/api
// Falls back to the prod API if unset so a mis-wired build never points at
// a broken/empty host. Each backend response is wrapped as `{ "data": ... }`;
// these helpers unwrap `.data` for the caller.
//
// Callers are fallback-first: every consumer wraps these calls in try/catch and
// returns its own safe fallback (empty array / null / hardcoded default) on any
// failure. These helpers therefore THROW on error and never swallow it.

const API_BASE = import.meta.env?.VITE_API_BASE || 'https://api.openbooq.com/api';

const unwrap = (json) => (json && typeof json === 'object' && 'data' in json ? json.data : json);

/**
 * GET `${VITE_API_BASE}/v1/website${path}` and return the unwrapped `.data`.
 * @param {string} path  e.g. '/reviews' or '/city-tiers'
 * @param {object} [opts]
 * @param {object} [opts.headers]  extra headers (e.g. Authorization)
 */
export const apiGet = async (path, { headers } = {}) => {
  try {
    const res = await fetch(`${API_BASE}/v1/website${path}`, {
      method: 'GET',
      headers: { Accept: 'application/json', ...(headers || {}) },
    });
    if (!res?.ok) {
      throw new Error(`GET /v1/website${path} failed: ${res?.status}`);
    }
    return unwrap(await res.json());
  } catch (err) {
    // Re-throw a normalized error; the calling component applies its fallback.
    throw err instanceof Error ? err : new Error(String(err));
  }
};

/**
 * POST a JSON body to `${VITE_API_BASE}/v1/website${path}` and return the
 * unwrapped `.data` (may be null for fire-and-forget endpoints).
 */
export const apiPost = async (path, body, { headers } = {}) => {
  try {
    const res = await fetch(`${API_BASE}/v1/website${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(headers || {}) },
      body: JSON.stringify(body ?? {}),
    });
    if (!res?.ok) {
      throw new Error(`POST /v1/website${path} failed: ${res?.status}`);
    }
    const json = await res.json().catch(() => null);
    return unwrap(json);
  } catch (err) {
    throw err instanceof Error ? err : new Error(String(err));
  }
};

/**
 * PATCH a JSON body to `${VITE_API_BASE}/v1/website${path}`. Used by admin
 * writes (requires an Authorization header with a Clerk JWT — not yet wired
 * on the website, so admin writes currently degrade to "sign-in required").
 */
export const apiPatch = async (path, body, { headers } = {}) => {
  try {
    const res = await fetch(`${API_BASE}/v1/website${path}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(headers || {}) },
      body: JSON.stringify(body ?? {}),
    });
    if (!res?.ok) {
      throw new Error(`PATCH /v1/website${path} failed: ${res?.status}`);
    }
    const json = await res.json().catch(() => null);
    return unwrap(json);
  } catch (err) {
    throw err instanceof Error ? err : new Error(String(err));
  }
};

export { API_BASE };
